import { updateFunnel } from 'API/linkedin-funnel';
import { getCoPilotRouteForPathAndTab } from 'features/co-pilot/co-pilot-routes';
import { Funnel } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import _ from 'lodash';
import { NavigationStates } from 'stores/app-bar-navigation-store';

interface Params {
  campaign: Funnel | null;
  fullLinkedinAccount?: LinkedinAccount;
  setActiveTab: (tab: NavigationStates) => void;
  pathname: string;
  navigate: (route: string) => void;
  resetCampaignBuilder: () => void;
  setStartCampaignSuccessDialog: (value: { open: boolean }) => void;
  setStartMessageApprovalCampaignSuccessDialog: (value: {
    open: boolean;
    campaign: Funnel | null;
    linkedinAccount: LinkedinAccount | null;
  }) => void;
  setStartImmediatelyDialog: (
    value: { open: boolean; loading: boolean } | ((prev: { open: boolean; loading: boolean }) => { open: boolean; loading: boolean })
  ) => void;
  setStartImmediatelySuccessDialog: (value: { open: boolean } | ((prev: { open: boolean }) => { open: boolean })) => void;
  handleOpenErrorDialog: (error: any, endpoint: string, method: string) => void;
}

const useStartCampaignSuccessFlow = ({
  campaign,
  fullLinkedinAccount,
  setActiveTab,
  pathname,
  navigate,
  resetCampaignBuilder,
  setStartCampaignSuccessDialog,
  setStartMessageApprovalCampaignSuccessDialog,
  setStartImmediatelyDialog,
  setStartImmediatelySuccessDialog,
  handleOpenErrorDialog,
}: Params) => {
  const navigateToProjects = () => {
    const route = getCoPilotRouteForPathAndTab(pathname, NavigationStates.PROJECTS);
    if (route) {
      navigate(route);
    } else {
      setActiveTab(NavigationStates.PROJECTS);
    }
  };

  const handleOpenStartCampaignSuccessDialog = () => {
    setStartCampaignSuccessDialog({ open: true });
  };

  const handleCloseStartCampaignSuccessDialog = () => {
    setStartCampaignSuccessDialog({ open: false });
    resetCampaignBuilder();
    navigateToProjects();
  };

  const handleOpenStartMessageApprovalCampaignSuccessDialog = (newCampaign: Funnel, newLinkedinAccount: LinkedinAccount) => {
    setStartMessageApprovalCampaignSuccessDialog({ open: true, campaign: newCampaign, linkedinAccount: newLinkedinAccount });
  };

  const handleCloseStartMessageApprovalCampaignSuccessDialog = () => {
    setStartMessageApprovalCampaignSuccessDialog({ open: false, campaign: null, linkedinAccount: null });
  };

  const handleCloseAndProceedStartMessageApprovalCampaignSuccessDialog = () => {
    handleCloseStartMessageApprovalCampaignSuccessDialog();
    resetCampaignBuilder();
    navigateToProjects();
  };

  const handleOpenStartImmediatelyDialog = () => {
    handleCloseStartMessageApprovalCampaignSuccessDialog();
    setStartImmediatelyDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseStartImmediatelyDialog = () => {
    setStartMessageApprovalCampaignSuccessDialog({ open: true, campaign, linkedinAccount: fullLinkedinAccount || null });
    setStartImmediatelyDialog({ open: false, loading: false });
  };

  const handleOpenStartImmediatelySuccessDialog = () => {
    setStartImmediatelySuccessDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseStartImmediatelySuccessDialog = () => {
    setStartImmediatelySuccessDialog({ open: false });
  };

  const handleStartCampaignImmediately = async () => {
    if (_.isNil(campaign)) return;

    setStartImmediatelyDialog((prev) => ({ ...prev, loading: true }));
    const res = await updateFunnel(campaign._id, { generateMessagesNow: true });

    if (!res.error) {
      setStartImmediatelyDialog({ open: false, loading: false });
      navigateToProjects();
      resetCampaignBuilder();
      handleOpenStartImmediatelySuccessDialog();
    } else {
      setStartImmediatelyDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:id', 'patch');
    }
  };

  return {
    handleOpenStartCampaignSuccessDialog,
    handleCloseStartCampaignSuccessDialog,
    handleOpenStartMessageApprovalCampaignSuccessDialog,
    handleCloseAndProceedStartMessageApprovalCampaignSuccessDialog,
    handleOpenStartImmediatelyDialog,
    handleCloseStartImmediatelyDialog,
    handleStartCampaignImmediately,
    handleCloseStartImmediatelySuccessDialog,
  };
};

export default useStartCampaignSuccessFlow;
